import { PageHeader, DataTable, LoadingState, FilterBar, EmptyState } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import StudentAvatar from '../components/StudentAvatar';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalanceWallet,
  AttachMoney,
  CalendarToday,
  Cancel,
  CheckCircle,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  Group,
  RadioButtonUnchecked,
  Refresh,
  School,
  Settings,
  Summarize,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const formatCurrency = (value) => `GH₵${(Number(value) || 0).toFixed(2)}`;
const DAY_KEYS = [
  { label: 'M', key: 'M' },
  { label: 'T', key: 'T' },
  { label: 'W', key: 'W' },
  { label: 'TH', key: 'TH' },
  { label: 'F', key: 'F' },
];
const DAY_NAMES = { M: 'Monday', T: 'Tuesday', W: 'Wednesday', TH: 'Thursday', F: 'Friday' };

const EMPTY_RECORD_SUMMARY = {
  totalAmount: 0,
  paidCount: 0,
  unpaidCount: 0,
  absentCount: 0,
  studentCount: 0,
  amountPerDay: 0,
  dailyTotals: [],
};

const extractWeekNumber = (weekLabel) => {
  const parsed = parseInt(String(weekLabel || '').replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const normalizeStatus = (value) => {
  if (value === true || value === 'present' || value === 'paid') return 'paid';
  if (value === false || value === 'absent') return 'absent';
  if (value === 'unpaid') return 'unpaid';
  return 'notmarked';
};

const ensureDays = (days = {}) => ({
  M: normalizeStatus(days.M),
  T: normalizeStatus(days.T),
  W: normalizeStatus(days.W),
  TH: normalizeStatus(days.TH),
  F: normalizeStatus(days.F),
});

const getNextStatus = (status) => {
  if (status === 'notmarked') return 'paid';
  if (status === 'paid') return 'unpaid';
  if (status === 'unpaid') return 'absent';
  return 'notmarked';
};

const getInitials = (name = '') => (
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ST'
);

const summarizeRecords = (records = [], amountPerDay = 0) => {
  let paidCount = 0;
  let unpaidCount = 0;
  let absentCount = 0;
  let totalFromRecords = 0;

  records.forEach((record) => {
    totalFromRecords += Number(record.total) || 0;

    DAY_KEYS.forEach((day) => {
      const status = normalizeStatus(record.days?.[day.key]);
      if (status === 'paid') paidCount += 1;
      if (status === 'unpaid') unpaidCount += 1;
      if (status === 'absent') absentCount += 1;
    });
  });

  return {
    totalAmount: totalFromRecords || paidCount * (Number(amountPerDay) || 0),
    paidCount,
    unpaidCount,
    absentCount,
    studentCount: records.length,
    amountPerDay: Number(amountPerDay) || 0,
  };
};

const getRecordSummaryFromPayload = (payload = {}, records = [], fallbackAmount = 0) => {
  const amountPerDay = Number(payload?.amountPerDay ?? fallbackAmount) || 0;
  const fallbackSummary = summarizeRecords(records, amountPerDay);

  return {
    totalAmount: Number(payload?.totalAmount) || fallbackSummary.totalAmount,
    paidCount: Number(payload?.paidCount) || fallbackSummary.paidCount,
    unpaidCount: Number(payload?.unpaidCount) || fallbackSummary.unpaidCount,
    absentCount: Number(payload?.absentCount) || fallbackSummary.absentCount,
    studentCount: Number(payload?.studentCount) || records.length,
    amountPerDay,
    dailyTotals: Array.isArray(payload?.dailyTotals) ? payload.dailyTotals : [],
  };
};

export default function ClassFeeConfigPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [classes, setClasses] = useState([]);
  const [classFeeBands, setClassFeeBands] = useState({});
  const [currency, setCurrency] = useState('GHS');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [term, setTerm] = useState('');
  const [allTerms, setAllTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);
  const [availableTerms, setAvailableTerms] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [week, setWeek] = useState('');
  const [records, setRecords] = useState([]);
  const [recordSummary, setRecordSummary] = useState(EMPTY_RECORD_SUMMARY);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [markingKey, setMarkingKey] = useState('');
  const [showFeeBandsForm, setShowFeeBandsForm] = useState(false);
  const [showSummaryTable, setShowSummaryTable] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [auditReportData, setAuditReportData] = useState(null);
  const [isFetchingAudit, setIsFetchingAudit] = useState(false);
  const [auditReportDayFilter, setAuditReportDayFilter] = useState('M');

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/class-fees/config');
      const nextClasses = Array.isArray(res.data?.classes) ? res.data.classes : [];
      setClasses(nextClasses);
      setSelectedClass((current) => current || nextClasses[0]?._id || '');
      setClassFeeBands(res.data?.classFeeBands || {});
      setCurrency(res.data?.currency || 'GHS');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load class fee setup';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const rows = useMemo(() => (
    classes.map((cls) => {
      const classId = String(cls._id);
      const band = classFeeBands[classId] || {};
      return {
        ...cls,
        classId,
        amount: band.amount ?? '',
        displayName: cls.displayName || cls.name || 'Class',
      };
    })
  ), [classes, classFeeBands]);

  const configuredCount = rows.filter((row) => Number(row.amount) > 0).length;
  const selectedClassName = rows.find((row) => row.classId === selectedClass)?.displayName || 'Selected class';
  const totalPaidDays = records.reduce((total, record) => total + (Number(record.daysPaid) || 0), 0);
  const participatingStudents = records.filter((record) => Number(record.daysPaid) > 0).length;
  const dailyTotals = useMemo(() => (
    DAY_KEYS.reduce((acc, day) => {
      const serverDailyTotals = Array.isArray(recordSummary.dailyTotals) ? recordSummary.dailyTotals : [];
      const serverTotal = serverDailyTotals.find((item) => (
        String(item?.key || item?.day || '').toUpperCase() === day.key
      ));
      const fallbackPaidCount = records.filter((record) => (
        normalizeStatus(record.days?.[day.key]) === 'paid'
      )).length;

      acc[day.key] = serverDailyTotals.length > 0
        ? Number(serverTotal?.amount) || 0
        : fallbackPaidCount * (Number(recordSummary.amountPerDay) || 0);
      return acc;
    }, {})
  ), [recordSummary.amountPerDay, recordSummary.dailyTotals, records]);

  const mapServerRecords = useCallback((payload) => (
    (payload?.breakdown || [])
      .map((item) => ({
        _id: String(item.studentId),
        studentName: item.studentName || 'Student',
        profilePicture: item.profilePicture,
        className: item.className || payload.className || selectedClassName,
        amountPerDay: Number(item.amountPerDay ?? payload.amountPerDay) || 0,
        total: Number(item.total) || 0,
        daysPaid: Number(item.daysPaid) || 0,
        days: ensureDays(item.days),
      }))
      .sort((a, b) => a.studentName.localeCompare(b.studentName))
  ), [selectedClassName]);

  const fetchSchoolId = useCallback(async () => {
    try {
      const res = await api.get('/school-info');
      const school = res.data?.school;
      setSchoolId(school?._id || school || '');
    } catch (err) {
      console.error('Failed to fetch school info:', err);
    }
  }, []);

  const fetchAcademicYears = useCallback(async () => {
    if (!schoolId) return;

    try {
      const res = await api.get('/term/years', {
        params: { school: schoolId },
      });
      const years = (res.data?.academicYears || []).sort((a, b) => b.localeCompare(a));
      setAvailableAcademicYears(years);
      setAcademicYear((current) => current || years[0] || '');
    } catch (err) {
      console.error('Failed to fetch academic years:', err);
      toast.error('Unable to load academic years');
    }
  }, [schoolId]);

  const fetchTermsForYear = useCallback(async (year) => {
    if (!schoolId || !year) return;

    try {
      const res = await api.get('/term/all', {
        params: { school: schoolId, academicYear: year },
      });
      const rawTerms = res.data?.allTerms || [];
      const termsData = rawTerms.filter((termItem) => (
        !termItem.academicYear || termItem.academicYear === year
      ));
      setAllTerms(termsData);

      const uniqueTerms = [...new Set(termsData.map((termItem) => termItem.term).filter(Boolean))].sort();
      setAvailableTerms(uniqueTerms);

      const currentTerm = termsData.find((termItem) => termItem.isCurrent) || termsData[0];
      setTerm(currentTerm?.term || '');
      setSelectedTermId(currentTerm?._id || '');
      setAvailableWeeks([]);
      setWeek('');
    } catch (err) {
      console.error('Failed to fetch terms:', err);
      toast.error('Unable to load terms');
    }
  }, [schoolId]);

  const fetchTermWeeks = useCallback(async () => {
    if (!schoolId || !academicYear || !term || !selectedTermId) {
      setAvailableWeeks([]);
      setWeek('');
      return;
    }

    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear,
          term,
          termId: selectedTermId,
        },
      });

      const weeksData = [...(res.data?.weeks || [])].sort((a, b) => (
        (Number(a.weekNumber) || 0) - (Number(b.weekNumber) || 0)
      ));
      const weekLabels = weeksData
        .map((weekItem) => weekItem.label || `Week ${weekItem.weekNumber}`)
        .filter(Boolean);
      const currentWeek = weeksData.find((weekItem) => weekItem.isCurrent);
      const currentWeekLabel = currentWeek?.label || (currentWeek ? `Week ${currentWeek.weekNumber}` : '');

      setAvailableWeeks(weekLabels);
      setWeek((current) => (
        current && weekLabels.includes(current)
          ? current
          : currentWeekLabel || weekLabels[0] || ''
      ));
    } catch (err) {
      console.error('Failed to fetch weeks:', err);
      setAvailableWeeks([]);
      setWeek('');
      toast.error('Unable to load weeks');
    }
  }, [academicYear, schoolId, selectedTermId, term]);

  const fetchClassFeeRecords = useCallback(async (silent = false) => {
    if (!selectedClass || !selectedTermId || !week) {
      setRecords([]);
      setRecordSummary(EMPTY_RECORD_SUMMARY);
      return;
    }

    try {
      if (!silent) setRecordsLoading(true);
      const res = await api.post('/class-fees/calculate', {
        classId: selectedClass,
        termId: selectedTermId,
        week: extractWeekNumber(week),
      });

      const nextRecords = mapServerRecords(res.data);
      const nextAmount = Number(res.data?.amountPerDay) || 0;
      setRecords(nextRecords);
      setRecordSummary(getRecordSummaryFromPayload(res.data, nextRecords, nextAmount));
    } catch (err) {
      console.error('Failed to load class fee records:', err);
      if (!silent) {
        setRecords([]);
        setRecordSummary(EMPTY_RECORD_SUMMARY);
        toast.error(err.response?.data?.message || 'Could not load class fee records');
      }
    } finally {
      if (!silent) setRecordsLoading(false);
    }
  }, [mapServerRecords, selectedClass, selectedTermId, week]);

  const fetchAuditReport = useCallback(async () => {
    if (!selectedTermId || !week || !schoolId) return;

    try {
      setIsFetchingAudit(true);
      const res = await api.get('/class-fees/audit-report', {
        params: {
          termId: selectedTermId,
          week: extractWeekNumber(week),
          day: auditReportDayFilter,
        },
      });

      if (res.data?.success) {
        setAuditReportData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch class fee audit report:', err);
      toast.error('Failed to load class fee audit report');
    } finally {
      setIsFetchingAudit(false);
    }
  }, [auditReportDayFilter, schoolId, selectedTermId, week]);

  useEffect(() => {
    fetchSchoolId();
  }, [fetchSchoolId]);

  useEffect(() => {
    if (schoolId) {
      fetchAcademicYears();
    }
  }, [fetchAcademicYears, schoolId]);

  useEffect(() => {
    if (schoolId && academicYear) {
      fetchTermsForYear(academicYear);
    }
  }, [academicYear, fetchTermsForYear, schoolId]);

  useEffect(() => {
    if (selectedTermId) {
      fetchTermWeeks();
    }
  }, [fetchTermWeeks, selectedTermId]);

  useEffect(() => {
    fetchClassFeeRecords();
  }, [fetchClassFeeRecords]);

  useEffect(() => {
    if (showAuditDialog) {
      fetchAuditReport();
    }
  }, [fetchAuditReport, showAuditDialog]);

  const handleAcademicYearChange = (nextYear) => {
    setAcademicYear(nextYear);
    setTerm('');
    setAllTerms([]);
    setSelectedTermId('');
    setAvailableTerms([]);
    setAvailableWeeks([]);
    setWeek('');
    setRecords([]);
    setRecordSummary(EMPTY_RECORD_SUMMARY);
  };

  const handleTermChange = (nextTerm) => {
    const selected = allTerms.find((termItem) => (
      termItem.term === nextTerm && termItem.academicYear === academicYear
    )) || allTerms.find((termItem) => termItem.term === nextTerm);

    setTerm(nextTerm);
    setSelectedTermId(selected?._id || '');
    setAvailableWeeks([]);
    setWeek('');
    setRecords([]);
    setRecordSummary(EMPTY_RECORD_SUMMARY);
  };

  const handleClassChange = (classId) => {
    setSelectedClass(classId);
    setRecords([]);
    setRecordSummary(EMPTY_RECORD_SUMMARY);
  };

  const handleToggleClassFeeStatus = async (studentId, dayKey) => {
    if (!selectedClass || !selectedTermId || !week) {
      toast.error('Select class, term, and week first');
      return;
    }

    const currentRecord = records.find((record) => String(record._id) === String(studentId));
    if (!currentRecord) return;

    const previousRecords = records;
    const nextStatus = getNextStatus(currentRecord.days?.[dayKey] || 'notmarked');
    const nextKey = `${studentId}_${dayKey}`;
    setMarkingKey(nextKey);

    const optimisticRecords = records.map((record) => {
      if (String(record._id) !== String(studentId)) return record;

      const nextDays = { ...record.days, [dayKey]: nextStatus };
      const daysPaid = Object.values(nextDays).filter((status) => status === 'paid').length;
      return {
        ...record,
        days: nextDays,
        daysPaid,
        total: daysPaid * (Number(record.amountPerDay || recordSummary.amountPerDay) || 0),
      };
    });
    setRecords(optimisticRecords);
    setRecordSummary((current) => ({
      ...current,
      ...summarizeRecords(optimisticRecords, current.amountPerDay || recordSummary.amountPerDay),
      dailyTotals: current.dailyTotals,
    }));

    try {
      const res = await api.post('/class-fees/mark-bulk', {
        classId: selectedClass,
        termId: selectedTermId,
        week: extractWeekNumber(week),
        marks: [{
          student: studentId,
          day: dayKey,
          status: nextStatus,
        }],
      });

      const nextRecords = mapServerRecords(res.data);
      const nextAmount = Number(res.data?.amountPerDay) || recordSummary.amountPerDay || 0;
      setRecords(nextRecords);
      setRecordSummary(getRecordSummaryFromPayload(res.data, nextRecords, nextAmount));
    } catch (err) {
      setRecords(previousRecords);
      setRecordSummary((current) => ({
        ...current,
        ...summarizeRecords(previousRecords, current.amountPerDay || recordSummary.amountPerDay),
        dailyTotals: current.dailyTotals,
      }));
      toast.error(err.response?.data?.message || 'Failed to save class fee mark');
    } finally {
      setMarkingKey('');
    }
  };

  const handleAmountChange = (classId, displayName, value) => {
    setClassFeeBands((prev) => ({
      ...prev,
      [classId]: {
        ...(prev[classId] || {}),
        className: displayName,
        amount: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payloadBands = {};
      rows.forEach((row) => {
        payloadBands[row.classId] = {
          className: row.displayName,
          amount: Math.max(0, Number(row.amount) || 0),
        };
      });

      const res = await api.post('/class-fees/config', {
        currency,
        classFeeBands: payloadBands,
      });

      setClassFeeBands(res.data?.classFeeBands || payloadBands);
      toast.success(res.data?.message || 'Class fee amounts saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save class fee amounts');
    } finally {
      setSaving(false);
    }
  };

  const getStatusMeta = (status) => {
    if (status === 'paid') {
      return {
        label: 'Paid',
        color: theme.palette.success.main,
        bg: alpha(theme.palette.success.main, 0.12),
        icon: <CheckCircle fontSize="small" />,
        next: 'Unpaid',
      };
    }

    if (status === 'unpaid') {
      return {
        label: 'Unpaid',
        color: theme.palette.warning.main,
        bg: alpha(theme.palette.warning.main, 0.16),
        icon: <ErrorOutline fontSize="small" />,
        next: 'Absent',
      };
    }

    if (status === 'absent') {
      return {
        label: 'Absent',
        color: theme.palette.error.main,
        bg: alpha(theme.palette.error.main, 0.12),
        icon: <Cancel fontSize="small" />,
        next: 'Not marked',
      };
    }

    return {
      label: 'Not marked',
      color: theme.palette.text.disabled,
      bg: alpha(theme.palette.action.disabled, 0.08),
      icon: <RadioButtonUnchecked fontSize="small" />,
      next: 'Paid',
    };
  };

  const renderStatusButton = (record, dayKey) => {
    const status = normalizeStatus(record.days?.[dayKey]);
    const meta = getStatusMeta(status);
    const key = `${record._id}_${dayKey}`;

    return (
      <Tooltip key={dayKey} title={`${meta.label}. Tap to mark ${meta.next}.`} disableTouchListener>
        <span>
          <IconButton
            size="small"
            onClick={() => handleToggleClassFeeStatus(record._id, dayKey)}
            disabled={recordsLoading || markingKey === key}
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              color: meta.color,
              bgcolor: meta.bg,
              '&:hover': {
                bgcolor: alpha(meta.color, 0.18),
              },
            }}
          >
            {markingKey === key ? <CircularProgress size={16} color="inherit" /> : meta.icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderMobileRecordCard = (record) => (
    <Card
      key={record._id}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: theme.palette.divider,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <StudentAvatar student={record} sx={{ bgcolor: theme.palette.primary.main, width: 38, height: 38, fontSize: '0.85rem' }}>
            {getInitials(record.studentName)}
          </StudentAvatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight="700" noWrap>
              {record.studentName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {record.daysPaid} paid days • {formatCurrency(record.total)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" spacing={1}>
          {DAY_KEYS.map((day) => (
            <Stack key={day.key} alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" fontWeight="700">
                {day.label}
              </Typography>
              {renderStatusButton(record, day.key)}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );

  const auditWeeklySummary = auditReportData?.weeklySummary?.dailyTotals?.length
    ? auditReportData.weeklySummary
    : {
      grandTotal: Number(recordSummary.totalAmount) || 0,
      totalPaid: null,
      totalUnpaid: null,
      dailyTotals: DAY_KEYS.map((day) => ({
        day: day.key,
        dayName: DAY_NAMES[day.key],
        grandTotal: Number(dailyTotals[day.key]) || 0,
        totalPaid: null,
        totalUnpaid: null,
      })),
    };

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Class Fee Management</>} description={<>Track student class fees based on daily payment marks</>} actions={<><Button variant="contained" startIcon={showFeeBandsForm ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowFeeBandsForm(!showFeeBandsForm)} disabled={loading} size={isMobile ? 'medium' : 'large'}>
                        {showFeeBandsForm ? 'Hide Fee Configuration' : 'Configure Fees'}
                      </Button>
        <Button variant="outlined" startIcon={<Summarize />} endIcon={showSummaryTable ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowSummaryTable(!showSummaryTable)} disabled={!records.length} size={isMobile ? 'medium' : 'large'}>
                        {showSummaryTable ? 'Hide Summary Table' : 'Show Summary Table'}
                      </Button>
        <Button variant="outlined" startIcon={recordsLoading ? <CircularProgress size={18} /> : <Refresh />} onClick={() => fetchClassFeeRecords()} disabled={!selectedClass || !selectedTermId || !week || recordsLoading} size={isMobile ? 'medium' : 'large'}>
                        Refresh Records
                      </Button>
        <Button variant="outlined" color="info" startIcon={<Summarize />} onClick={() => setShowAuditDialog(true)} disabled={!selectedTermId || !week} size={isMobile ? 'medium' : 'large'}>
                        Audit Report
                      </Button></>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6,
            },
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.primary.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                }}>
                  <AttachMoney fontSize={isMobile ? 'small' : 'medium'} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? 'body2' : 'h6'} color="text.secondary">
                    Total Collected
                  </Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                    {formatCurrency(recordSummary.totalAmount)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Collected for selected class
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6,
            },
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.info.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                }}>
                  <Group fontSize={isMobile ? 'small' : 'medium'} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? 'body2' : 'h6'} color="text.secondary">
                    Paid Days
                  </Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                    {totalPaidDays}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total paid day marks
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SummaryMetric sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: isMobile ? 2 : 6,
            },
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.success.main,
                  width: isMobile ? 40 : 48,
                  height: isMobile ? 40 : 48,
                }}>
                  <Group fontSize={isMobile ? 'small' : 'medium'} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? 'body2' : 'h6'} color="text.secondary">
                    Number of Students
                  </Typography>
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
                    {participatingStudents}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Students with paid marks
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SummaryMetric>
        </Grid>
      </SummaryStrip>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Filter Card */}
      <FilterBar sx={{
        mb: isMobile ? 3 : 4,
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`,
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: isMobile ? 32 : 40,
                height: isMobile ? 32 : 40,
              }}>
                <CalendarToday fontSize={isMobile ? 'small' : 'medium'} />
              </Avatar>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600">
                Fee Setup & Filters
              </Typography>
            </Stack>
          </Stack>

          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Year"
                value={availableAcademicYears.includes(academicYear) ? academicYear : ''}
                onChange={(event) => handleAcademicYearChange(event.target.value)}
                disabled={loading || availableAcademicYears.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              >
                <MenuItem value="" disabled>Select Academic Year</MenuItem>
                {availableAcademicYears.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Academic Term"
                value={availableTerms.includes(term) ? term : ''}
                onChange={(event) => handleTermChange(event.target.value)}
                disabled={loading || availableTerms.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              >
                <MenuItem value="" disabled>Select Academic Term</MenuItem>
                {availableTerms.map((termOption) => (
                  <MenuItem key={termOption} value={termOption}>{termOption}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Class"
                value={selectedClass}
                onChange={(event) => handleClassChange(event.target.value)}
                disabled={loading || rows.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              >
                <MenuItem value="" disabled>Select Class</MenuItem>
                {rows.map((row) => (
                  <MenuItem key={row.classId} value={row.classId}>
                    {row.displayName}
                    {Number(row.amount) > 0 && (
                      <Chip
                        size="small"
                        label={formatCurrency(row.amount)}
                        color="success"
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
              <TextField
                select
                fullWidth
                label="Week"
                value={availableWeeks.includes(week) ? week : ''}
                onChange={(event) => setWeek(event.target.value)}
                disabled={loading || availableWeeks.length === 0}
                InputLabelProps={{ shrink: true }}
                size={isMobile ? 'small' : 'medium'}
              >
                <MenuItem value="" disabled>Select Week</MenuItem>
                {availableWeeks.map((weekOption) => (
                  <MenuItem key={weekOption} value={weekOption}>{weekOption}</MenuItem>
                ))}
              </TextField>
            </Grid>


          </Grid>
        </CardContent>
      </FilterBar>

      {/* Fee Configuration Card */}
      <Collapse in={showFeeBandsForm}>
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{
                  bgcolor: theme.palette.secondary.main,
                  width: isMobile ? 32 : 40,
                  height: isMobile ? 32 : 40,
                }}>
                  <Settings fontSize={isMobile ? 'small' : 'medium'} />
                </Avatar>
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="600">
                  Configure Class Fees
                </Typography>
              </Stack>
            </Stack>

            <Typography variant={isMobile ? 'h6' : 'h6'} gutterBottom>
              Set Daily Fee Rates by Class
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Each class can have its own specific daily rate
            </Typography>

            <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Alert severity="info" icon={<School />} sx={{ height: '100%', alignItems: 'center' }}>
                  <Typography variant="body2">
                    Set each class daily amount once. Teachers will use these rates when marking paid, unpaid, and absent records.
                  </Typography>
                </Alert>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  fullWidth
                  label="Currency"
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                  disabled={loading || saving}
                  InputLabelProps={{ shrink: true }}
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>
            </Grid>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" p={4}>
                <CircularProgress />
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                  Loading classes...
                </Typography>
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <School sx={{
                  fontSize: isMobile ? 48 : 64,
                  color: 'text.secondary',
                  opacity: 0.5,
                  mb: 2,
                }} />
                <Typography variant={isMobile ? 'h6' : 'h6'} color="text.secondary" gutterBottom>
                  No classes found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add classes first, then return to configure class fee amounts.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={isMobile ? 1 : 2}>
                {rows.map((row) => (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={row.classId}>
                    <Card variant="outlined" sx={{ p: isMobile ? 1 : 2 }}>
                      <Stack spacing={isMobile ? 1 : 2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} fontWeight="500">
                            {row.displayName}
                          </Typography>
                          <Chip
                            size="small"
                            label={Number(row.amount) > 0 ? 'Configured' : 'Not set'}
                            color={Number(row.amount) > 0 ? 'success' : 'default'}
                          />
                        </Stack>
                        <TextField
                          fullWidth
                          size={isMobile ? 'small' : 'medium'}
                          label="Daily Fee Amount"
                          type="number"
                          value={row.amount}
                          onChange={(event) => handleAmountChange(row.classId, row.displayName, event.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">GH₵</InputAdornment>
                            ),
                            inputProps: {
                              min: 0,
                              step: 0.01,
                            },
                          }}
                          helperText="Set to 0 to leave this class unset"
                        />
                      </Stack>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || loading}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                size={isMobile ? 'medium' : 'large'}
              >
                {saving ? 'Saving...' : 'Save Class Fees'}
              </Button>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Class-Wide Daily Summary */}
      <Card sx={{
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: `1px solid ${theme.palette.primary.main}`,
      }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="primary.main" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <School fontSize={isMobile ? 'small' : 'medium'} /> Class Daily Collection (Week {week ? week.replace('Week ', '') : '-'})
            </Typography>
            <Chip
              label={`Grand Total: ${formatCurrency(recordSummary.totalAmount)}`}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
              sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.85rem' : '1rem' }}
            />
          </Stack>

          <Grid container spacing={1} justifyContent="center" columns={{ xs: 12, sm: 10 }}>
            {DAY_KEYS.map((day) => (
              <Grid size={{ xs: 6, sm: 2 }} key={day.key}>
                <Paper className="workspace-section" elevation={0} sx={{
                  p: isMobile ? 0.5 : 1.5,
                  textAlign: 'center',
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                }}>
                  <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold">
                    {day.key === 'M' ? 'MON' : day.key === 'T' ? 'TUE' : day.key === 'W' ? 'WED' : day.key === 'TH' ? 'THU' : 'FRI'}
                  </Typography>
                  <Typography variant={isMobile ? 'body2' : 'h6'} color="success.main" fontWeight="bold" sx={{ fontSize: isMobile ? '0.85rem' : undefined }}>
                    {formatCurrency(dailyTotals[day.key] || 0)}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      

      {/* Configuration Type Indicator */}
      <Alert severity="success" sx={{ mb: 3 }} icon={<Group />}>
        <Typography variant="body2">
          Using <strong>Class-Specific</strong> fee configuration
        </Typography>
      </Alert>

      {/* Class Fees Applied */}
      {configuredCount > 0 && (
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <CardContent>
            <Typography variant={isMobile ? 'h6' : 'h6'} gutterBottom fontWeight="600">
              Class Fees Applied
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rows
                .filter((row) => Number(row.amount) > 0)
                .map((row) => (
                  <Chip
                    key={row.classId}
                    label={`${row.displayName}: ${formatCurrency(row.amount)}`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? 'small' : 'medium'}
                  />
                ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Term Info */}
      {term && academicYear && week && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {term} {academicYear} • Week {week.replace('Week ', '')}
          </Typography>
        </Alert>
      )}

      {/* Summary Table */}
      <Collapse in={showSummaryTable && records.length > 0}>
        <Card sx={{
          mb: isMobile ? 3 : 4,
          boxShadow: isMobile ? 1 : 3,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{
              p: isMobile ? 2 : 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
              <Typography variant={isMobile ? 'h6' : 'h6'} fontWeight="600">
                Detailed Breakdown
              </Typography>
            </Box>
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '40vh' : 'none',
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 600 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Class</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Days Paid</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Daily Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{records.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {records.map((record) => (
                    <TableRow key={record._id}>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{record.studentName}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{record.className || selectedClassName}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">{record.daysPaid || 0}</TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">
                        {formatCurrency(record.amountPerDay || recordSummary.amountPerDay)}
                      </TableCell>
                      <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} align="right">
                        <Typography fontWeight="500">
                          {formatCurrency(record.total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Detailed Records */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`,
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? 'h6' : 'h6'} fontWeight="600">
                Class Fee Records ({records.length})
              </Typography>
              {records.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: isMobile ? 1 : 0 }}
                >
                  <Chip
                    label={`Total: ${formatCurrency(recordSummary.totalAmount)}`}
                    color="primary"
                    variant="outlined"
                    size={isMobile ? 'small' : 'medium'}
                  />
                  <Chip
                    label={`${totalPaidDays} days paid`}
                    color="success"
                    variant="outlined"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          {loading || recordsLoading ? (
            <LoadingState />
          ) : !selectedClass || !selectedTermId || !week ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <AccountBalanceWallet sx={{
                fontSize: isMobile ? 48 : 64,
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2,
              }} />
              <Typography variant={isMobile ? 'h6' : 'h6'} color="text.secondary" gutterBottom>
                Select filters to view class fee records
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose academic year, term, class, and week.
              </Typography>
            </Box>
          ) : records.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <AccountBalanceWallet sx={{
                fontSize: isMobile ? 48 : 64,
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2,
              }} />
              <Typography variant={isMobile ? 'h6' : 'h6'} color="text.secondary" gutterBottom>
                No fee records found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No records found for {selectedClassName} in {week} of {term} {academicYear}
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {records.map((record) => renderMobileRecordCard(record))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none',
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>CLASS</TableCell>
                    {DAY_KEYS.map((day) => (
                      <TableCell key={day.key} align="center" sx={{ fontWeight: 'bold', py: 2 }}>
                        {day.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">DAYS PAID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">DAILY RATE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">AMOUNT</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
{records.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {records.map((record) => (
                    <TableRow
                      key={record._id}
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <StudentAvatar student={record} sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40, fontSize: '0.85rem' }}>
                            {getInitials(record.studentName)}
                          </StudentAvatar>
                          <Typography variant="subtitle1" fontWeight="500">
                            {record.studentName}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={record.className || selectedClassName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      {DAY_KEYS.map((day) => (
                        <TableCell key={day.key} align="center" sx={{ py: 1 }}>
                          {renderStatusButton(record, day.key)}
                        </TableCell>
                      ))}

                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography variant="subtitle1" fontWeight="500">
                          {record.daysPaid}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography variant="subtitle1" fontWeight="500">
                          {formatCurrency(record.amountPerDay || recordSummary.amountPerDay)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 2 }} align="right">
                        <Typography variant="subtitle1" fontWeight="500" color="success.main">
                          {formatCurrency(record.total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showAuditDialog}
        onClose={() => setShowAuditDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              Daily Audit Report
            </Typography>
            <Box>
              <Tabs
                value={Math.max(DAY_KEYS.findIndex((day) => day.key === auditReportDayFilter), 0)}
                onChange={(event, value) => setAuditReportDayFilter(DAY_KEYS[value].key)}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
              >
                {DAY_KEYS.map((day) => (
                  <Tab key={day.key} label={day.label} />
                ))}
              </Tabs>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: 'background.paper',
            p: isMobile ? 1 : 4,
            '@media print': { p: 0, m: 0, border: 'none' },
          }}
        >
          <style>
            {`
              @media print {
                body * { visibility: hidden; }
                #printable-class-fee-audit-report, #printable-class-fee-audit-report * { visibility: visible; }
                #printable-class-fee-audit-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
                .MuiDialogActions-root { display: none !important; }
                .MuiDialogTitle-root { display: none !important; }
                .MuiDialog-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; max-width: none !important; max-height: none !important; }
                #printable-class-fee-audit-report td,
                #printable-class-fee-audit-report th,
                #printable-class-fee-audit-report p,
                #printable-class-fee-audit-report span,
                #printable-class-fee-audit-report .MuiTypography-root,
                #printable-class-fee-audit-report .MuiTableCell-root {
                  color: #000 !important;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                #printable-class-fee-audit-report .audit-status-paid { color: #145a32 !important; font-weight: bold; }
                #printable-class-fee-audit-report .audit-status-unpaid { color: #7b241c !important; font-weight: bold; }
                #printable-class-fee-audit-report .audit-status-other { color: #333 !important; font-weight: bold; }
              }
            `}
          </style>

          <Box id="printable-class-fee-audit-report">
            {isFetchingAudit ? (
              <LoadingState />
            ) : auditReportData && (auditReportData.report || auditReportData.dailyReports) ? (
              <Stack spacing={4} sx={{ color: '#000' }}>
                <Box sx={{ textAlign: 'center', borderBottom: '2px solid #000', pb: 2 }}>
                  <Typography variant="h5" fontWeight="bold" textTransform="uppercase">
                    Daily Class Fee Audit Report
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Academic Year: {academicYear || 'N/A'} | Term: {term || 'N/A'} | Week: {week ? week.replace('Week ', '') : 'N/A'}
                  </Typography>
                  <Typography variant="subtitle2">
                    Reporting Day: {DAY_NAMES[auditReportDayFilter] || auditReportDayFilter}
                  </Typography>
                </Box>

                {auditReportDayFilter === 'M' && auditWeeklySummary?.dailyTotals?.length > 0 && (
                  <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                    <DataTable><Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                          <TableCell colSpan={4} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                            WHOLE WEEK COLLECTION SUMMARY
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '30%' }}>Day</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Paid Pupils</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Unpaid Pupils</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '30%' }}>Collected (GH₵)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {auditWeeklySummary.dailyTotals.map((dayTotal) => (
                          <TableRow key={dayTotal.day}>
                            <TableCell>{dayTotal.dayName || DAY_NAMES[dayTotal.day] || dayTotal.day}</TableCell>
                            <TableCell align="center">{dayTotal.totalPaid ?? '-'}</TableCell>
                            <TableCell align="center">{dayTotal.totalUnpaid ?? '-'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {Number(dayTotal.grandTotal || 0).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>WEEK TOTAL</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{auditWeeklySummary.totalPaid ?? '-'}</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>{auditWeeklySummary.totalUnpaid ?? '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {Number(auditWeeklySummary.grandTotal || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table></DataTable>
                  </Paper>
                )}

                <Paper elevation={0} sx={{ border: '1px solid #000', borderRadius: 0 }}>
                  <DataTable><Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                        <TableCell colSpan={3} align="center" sx={{ borderBottom: '1px solid #000', fontWeight: 'bold' }}>
                          EXECUTIVE SUMMARY
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Paid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #ccc', width: '33%' }}>Total Unpaid Pupils</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', width: '34%' }}>Grand Total Collected (GH₵)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalPaid}</TableCell>
                        <TableCell align="center" sx={{ borderRight: '1px solid #ccc' }}>{auditReportData.totalUnpaid}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                          {Number(auditReportData.grandTotal || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table></DataTable>
                </Paper>

                {([{ day: auditReportData.day, report: auditReportData.report, grandTotal: auditReportData.grandTotal, totalPaid: auditReportData.totalPaid, totalUnpaid: auditReportData.totalUnpaid }]).map((dayData, index) => (
                  <Box key={dayData.day || index}>
                    {!dayData.report || dayData.report.length === 0 ? (
                      <Typography textAlign="center" color="text.secondary" p={3}>
                        No class fee records found for this day.
                      </Typography>
                    ) : (
                      dayData.report.map((classReport) => (
                        <Paper key={classReport.classId} elevation={0} sx={{ border: '1px solid #000', borderRadius: 0, mb: 3 }}>
                          <DataTable><Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                                <TableCell colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                                  CLASS: {classReport.className}
                                </TableCell>
                                <TableCell align="right" colSpan={2} sx={{ fontWeight: 'bold', borderBottom: '1px solid #000' }}>
                                  Class Collection: GH₵{Number(classReport.totalAmount || 0).toFixed(2)} | Paid: {classReport.paidCount} | Unpaid: {classReport.unpaidCount}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '40%' }}>Student Name</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '15%' }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '20%' }}>Amount (GH₵)</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', borderBottom: '1px solid #666', width: '25%' }}>Parent Contact</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(classReport.students || []).map((student) => {
                                const isPaid = student.status === 'paid';
                                const isUnpaid = student.status === 'unpaid';
                                const statusLabel = isPaid ? 'PAID' : isUnpaid ? 'UNPAID' : 'NOT MARKED';
                                const statusClass = isPaid ? 'audit-status-paid' : isUnpaid ? 'audit-status-unpaid' : 'audit-status-other';
                                const statusColor = isPaid ? '#145a32' : isUnpaid ? '#922b21' : '#555';

                                return (
                                  <TableRow key={student.studentId} sx={{ '& td': { borderBottom: '1px solid #eee' } }}>
                                    <TableCell sx={{ color: '#000' }}>{student.studentName}</TableCell>
                                    <TableCell align="center" className={statusClass} sx={{ color: statusColor, fontWeight: 'bold' }}>
                                      {statusLabel}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: '#000' }}>
                                      {Number(student.amount || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ color: '#000' }}>
                                      {student.guardianName || student.guardianPhone ? (
                                        `${student.guardianName ? `${student.guardianName} - ` : ''}${student.guardianPhone || ''}`
                                      ) : (
                                        'N/A'
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {(!classReport.students || classReport.students.length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">No students recorded in this class.</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table></DataTable>
                        </Paper>
                      ))
                    )}
                  </Box>
                ))}

                <Box sx={{ mt: 6, pt: 4, display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #ccc' }}>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Prepared By (Accountant)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                  <Box sx={{ width: '30%', textAlign: 'center' }}>
                    <Box sx={{ borderBottom: '1px solid #000', height: '40px' }} />
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>Approved By (Admin/Principal)</Typography>
                    <Typography variant="caption">Date: _________________</Typography>
                  </Box>
                </Box>
              </Stack>
            ) : (
              <Typography textAlign="center" color="text.secondary" p={3}>
                Select a day to load the class fee audit report.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAuditDialog(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const weekNum = week ? week.replace('Week ', '') : '';
              const previousTitle = document.title;
              const dayName = DAY_NAMES[auditReportDayFilter] || auditReportDayFilter;
              document.title = `Class Fee Audit Report - Week ${weekNum} - ${dayName}`;
              window.print();
              setTimeout(() => { document.title = previousTitle; }, 1000);
            }}
          >
            Print / Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
